import { createContext, type ReactNode, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { bookedAlready } from "../../src/booking.ts";
import { tripStart } from "../../src/days.ts";
import { api, type Item, type TripFile, type TripView, type WeatherDay, type When } from "./api.ts";
import { promptLead, promptWith } from "./ask-ai.ts";
import { SYMBOL_ICON, WeatherCredit } from "./DayWeather.tsx";
import { CloseCard } from "./Details.tsx";
import { Modal } from "./Dialog.tsx";
import { type DeckRun, startRun } from "./deck.ts";
import { drawable, Viewer } from "./Files.tsx";
import { itemKind, km, localToday, weekday } from "./format.ts";
import { type LngLat, point } from "./geo.ts";
import { haptic } from "./haptics.ts";
import { heicToJpeg, isHeic, useDrawsHeic } from "./heic.ts";
import { useKept, useOnline } from "./offline.ts";
import { useReadOnly } from "./readOnly.ts";
import { Button, Icon } from "./souvenir.ts";
import { TodoRow } from "./Todos.tsx";
import {
  type AroundList,
  type AroundRow,
  inTodayWindow,
  type NextUp,
  nothingFixedWords,
  pickToday,
  type TodayPicks,
  type TodayRow,
  untilWords,
  whoseWords,
} from "./today.ts";
import { factLine, KindIcon, Prompt } from "./ui.tsx";
import { fileHref } from "./viewing.ts";
import { current, degrees, SYMBOL_LABEL, type Unit, unitFor } from "./weather.ts";

// Today, during the trip (DESIGN.md, Views, "Today, during the trip"; features/on-the-road.md): the day from
// my side, over the view I'm on (`?today=1`): a sheet over the whole screen on a phone, the panel at the
// right on a computer. From the top: the day and where we are with its weather, Next up, what's on today,
// what we want around us, what waits for my rating here, tonight's stay and tomorrow. What it picks is
// today.ts; this draws it. Every row opens its card over Today.

// Around us, this many at a time.
const AT_A_TIME = 4;

// The clock, moving on each minute: Next up's "in 2 h 20 min", and the day turning over at midnight.
function useMinute(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(tick);
  }, []);
  return now;
}

// Whether Today is offered now, on this device's day: from the day before the trip to the day after.
export function useTodayWindow(trip: TripView): boolean {
  const now = useMinute();
  return inTodayWindow(trip.dates, localToday(now));
}

// The floating Today button: on a phone above the tabs (beside List on the map), on a computer in the trip's
// bar. The sun, in its colour.
export function TodayButton({ onClick, compact = false }: { onClick: () => void; compact?: boolean }) {
  return (
    <button type="button" className={`tm-today-button${compact ? " is-compact" : ""}`} onClick={onClick}>
      <Icon name="sun" size={20} />
      <span className="tm-today-label">Today</span>
    </button>
  );
}

export function Today({
  trip,
  items: loaded,
  files,
  weather,
  mornings,
  me,
  wide,
  onOpen,
  onClose,
  onRate,
  onAllDays,
  onAllOnMap,
  onChanged,
}: {
  trip: TripView;
  items: Item[];
  files: TripFile[];
  weather: WeatherDay[] | null;
  mornings: WeatherDay[] | null;
  // Where I am, while Where I am is on.
  me: LngLat | null;
  wide: boolean;
  // A row tapped: its card in full, over Today.
  onOpen: (id: string) => void;
  onClose: () => void;
  // Rate them: the deck, at this area.
  onRate: (run: DeckRun) => void;
  onAllDays: () => void;
  onAllOnMap: (ids: string[]) => void;
  // The trip changed from here: load it again.
  onChanged: () => void;
}) {
  const now = useMinute();
  const date = localToday(now);
  // My one-tap changes, on the trip as loaded until it's loaded again with them.
  const taps = useTaps(trip, loaded, date, onChanged);
  const items = taps.items;
  // Picked again each minute, as the day goes on (what's next, how long until it).
  const minute = Math.floor(now.getTime() / 60_000);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `minute` stands for `now`, which moves every render
  const picks = useMemo(
    () =>
      pickToday({
        items,
        members: trip.members,
        you: trip.you,
        dates: trip.dates,
        date,
        now,
        me,
        hour: now.getHours(),
      }),
    [items, trip.members, trip.you, trip.dates, date, minute, me],
  );
  const headingId = useId();
  const filesOf = (id: string) => files.filter((f) => f.on === id && f.as === "file");
  const rate = () => {
    if (!picks.rate) return;
    const run = startRun(items, trip.members, trip.you, tripStart(trip.dates));
    onRate({ ...run, at: picks.rate.index });
  };
  const anything = Boolean(picks.around?.lists.some((l) => l.rows.length > 0));
  // A read-only trip: today to read, none of its taps (nothing done, moved, rated or added).
  const readOnly = useReadOnly();
  return (
    <Taps.Provider value={taps}>
      <div className="tm-today">
        <TodayHead picks={picks} weather={weather} mornings={mornings} headingId={headingId} now={now}>
          {wide && <CloseCard onClick={onClose} />}
        </TodayHead>
        {picks.howWasToday && !readOnly && (
          <HowWasToday
            trip={trip.title}
            asked={picks.howWasToday}
            area={(picks.evening ?? picks.morning)?.name ?? null}
          />
        )}
        {picks.next && <NextUpCard next={picks.next} now={now} files={filesOf(picks.next.item.id)} onOpen={onOpen} />}
        <section className="tm-today-section" aria-labelledby={`${headingId}-today`}>
          <h2 id={`${headingId}-today`} className="title">
            Today
          </h2>
          {picks.rows.length === 0 ? (
            <p className="callout tm-muted">{nothingFixedWords(trip.members.length, anything)}</p>
          ) : (
            <ul className="tm-today-rows">
              {picks.rows.map((row) =>
                row.kind === "todo" ? (
                  <TodoRow key={row.item.id} item={row.item} trip={trip} onOpen={onOpen} calm />
                ) : (
                  <TodayLine key={`${row.kind}-${row.item.id}`} row={row} onOpen={onOpen} />
                ),
              )}
            </ul>
          )}
        </section>
        {picks.around && <Around picks={picks} trip={trip} me={me !== null} onOpen={onOpen} onAllOnMap={onAllOnMap} />}
        {picks.rate && !readOnly && (
          <section className="tm-today-rate" aria-label="Not rated by you yet">
            <p className="callout">
              <strong>
                {picks.rate.area.ids.length} {inArea(picks.rate.area.label)} you haven't rated:
              </strong>{" "}
              <span className="tm-muted">{rateNames(picks.rate.area.ids, items)}</span>
            </p>
            <div className="tm-today-buttons">
              <Button variant="secondary" size="sm" type="button" onClick={rate}>
                Rate them
              </Button>
            </div>
          </section>
        )}
        {picks.tonight && <Tonight tonight={picks.tonight} onOpen={onOpen} />}
        {picks.tomorrow && (
          <div className="tm-today-tomorrow">
            <p className="callout tm-today-tomorrow-text">
              <strong>Tomorrow</strong>
              <span className="tm-muted"> · {tomorrowWords(picks.tomorrow)}</span>
            </p>
            <button type="button" className="tm-link-button callout" onClick={onAllDays}>
              All days
            </button>
          </div>
        )}
        <WeatherCredit oldest={null} now={now} />
        {taps.said && (
          <Said key={taps.said.key} said={taps.said} trip={trip.id} onUndone={taps.undone} onGone={taps.quiet} />
        )}
      </div>
    </Taps.Provider>
  );
}

// ---------- One tap ----------

// What a one-tap change will make of a thing, shown at once and until the trip loads with it: its status,
// its time (null: none), and that I'm going.
interface Patch {
  status?: Item["status"];
  when?: When | null;
  going?: true;
}

// What Today says after a change, for a while: what it did and its Undo, or why it couldn't.
interface Saying {
  key: number;
  text: string;
  change: string | null;
  // The thing it changed, put back as loaded if it's undone.
  item: string | null;
}

interface TapsState {
  items: Item[];
  said: Saying | null;
  done: (item: Item, e: { nativeEvent: object }) => void;
  back: (item: Item) => void;
  onToday: (item: Item) => void;
  anotherDay: (item: Item) => void;
  missed: (item: Item) => void;
  unmissed: (item: Item) => void;
  photo: (item: Item, file: File) => Promise<void>;
  undone: () => void;
  quiet: () => void;
}

const Taps = createContext<TapsState | null>(null);
function useTapsHere(): TapsState {
  const taps = useContext(Taps);
  if (!taps) throw new Error("Today's taps are made inside Today");
  return taps;
}

// Today's one-tap changes (DESIGN.md, "Today, during the trip"; the log's "One tap on the road"): each one
// write through "web" (api.today), shown at once, with Undo after it; without signal, saying it needs signal.
function useTaps(trip: TripView, loaded: Item[], date: string, onChanged: () => void): TapsState {
  const [pending, setPending] = useState<ReadonlyMap<string, { patch: Patch; until: number }>>(new Map());
  const [said, setSaid] = useState<Saying | null>(null);
  const sayings = useRef(0);
  // One write after another: two quick taps on one thing never cross.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const online = useOnline();
  const kept = useKept();
  // What the loads have caught up with goes.
  useEffect(() => {
    const versions = new Map(loaded.map((i) => [i.id, i.version]));
    setPending((m) => {
      const left = [...m].filter(([id, p]) => (versions.get(id) ?? Number.POSITIVE_INFINITY) < p.until);
      return left.length === m.size ? m : new Map(left);
    });
  }, [loaded]);
  const items =
    pending.size === 0
      ? loaded
      : loaded.map((i) => {
          const p = pending.get(i.id)?.patch;
          if (!p) return i;
          const next: Item = { ...i };
          if (p.status) next.status = p.status;
          if (p.when === null) delete next.when;
          else if (p.when) next.when = p.when;
          if (p.going) {
            const mine = i.interest.find((x) => x.person === trip.you);
            next.interest = mine
              ? i.interest.map((x) => (x.person === trip.you ? { ...x, going: "yes" } : x))
              : [...i.interest, { person: trip.you, tier: null, going: "yes" }];
          }
          return next;
        });
  const say = (text: string, change: string | null, item: string | null) =>
    setSaid({ key: ++sayings.current, text, change, item });
  const offline = !online || kept !== null;
  const change = (item: Item, edit: Parameters<typeof api.today>[2], patch: Patch, words: string) => {
    if (offline) return say("No signal: changes need signal.", null, null);
    setPending((m) => new Map(m).set(item.id, { patch, until: Number.POSITIVE_INFINITY }));
    const run = queue.current.then(() => api.today(trip.id, item.id, edit));
    queue.current = run.catch(() => {});
    run.then(
      ({ change, version }) => {
        setPending((m) => {
          const p = m.get(item.id);
          return p ? new Map(m).set(item.id, { ...p, until: version }) : m;
        });
        if (change) say(words, change, item.id);
        onChanged();
      },
      (e: unknown) => {
        setPending((m) => {
          const next = new Map(m);
          next.delete(item.id);
          return next;
        });
        say(`Couldn't save that: ${e instanceof Error ? e.message : "try again."}`, null, null);
      },
    );
  };
  const partOf = (item: Item) => (item.when?.precision === "part" ? item.when.part : null);
  return {
    items,
    said,
    done: (item, e) => {
      haptic("commit", e);
      change(item, { done: true }, { status: "done" }, `Done: ${item.title}`);
    },
    // Back to what it was before (the server reads it from its history): planned, shown meanwhile.
    back: (item) => change(item, { done: false }, { status: "planned" }, `Not done: ${item.title}`),
    onToday: (item) => {
      const part = partOf(item);
      const when: When = part ? { precision: "part", day: date, part } : { precision: "day", day: date };
      change(item, { day: date }, { when, going: true }, `On today: ${item.title}`);
    },
    anotherDay: (item) => {
      const part = partOf(item);
      change(item, { day: null }, { when: part ? { precision: "part", part } : null }, `Another day: ${item.title}`);
    },
    missed: (item) => change(item, { missed: true }, { status: "missed" }, `Not this time: ${item.title}`),
    // Back in the plan: to what it was before Not this time (the server reads it from its history), planned
    // meanwhile.
    unmissed: (item) => change(item, { missed: false }, { status: "planned" }, `Back in the plan: ${item.title}`),
    photo: async (item, picked) => {
      if (offline) return say("No signal: changes need signal.", null, null);
      const file = isHeic(picked) ? ((await heicToJpeg(picked)) ?? picked) : picked;
      try {
        await api.attachFile(trip.id, item.id, file, "photo");
        say(`Photo added: ${item.title}`, null, null);
        onChanged();
      } catch (e) {
        say(`Couldn't add the photo: ${e instanceof Error ? e.message : "try again."}`, null, null);
      }
    },
    // Undone: what it changed shows as loaded again, until the trip loads with the undo.
    undone: () => {
      const id = said?.item;
      if (id)
        setPending((m) => {
          const next = new Map(m);
          next.delete(id);
          return next;
        });
      onChanged();
    },
    quiet: () => setSaid(null),
  };
}

// What a change did, with its Undo (as the deck's), for 10 seconds; or why it couldn't, until put away.
function Said({
  said,
  trip,
  onUndone,
  onGone,
}: {
  said: Saying;
  trip: string;
  onUndone: () => void;
  onGone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gone = useRef(onGone);
  gone.current = onGone;
  const online = useOnline();
  useEffect(() => {
    if (error) return;
    const timer = setTimeout(() => gone.current(), 10_000);
    return () => clearTimeout(timer);
  }, [error]);
  const undo = async () => {
    if (!said.change) return;
    if (!online) return setError("No signal: undoing needs signal.");
    setBusy(true);
    try {
      await api.undo(trip, said.change);
      onUndone();
      gone.current();
    } catch (e) {
      setBusy(false);
      setError(`Couldn't undo: ${e instanceof Error ? e.message : "try again from Updates."}`);
    }
  };
  return (
    <div className="tm-today-said" role="status">
      <span className="callout tm-today-said-text">{error ?? said.text}</span>
      {said.change && !error ? (
        <Button variant="primary" size="sm" disabled={busy} onClick={undo}>
          {busy ? "Undoing…" : "Undo"}
        </Button>
      ) : (
        <Button variant="primary" size="sm" onClick={() => gone.current()}>
          OK
        </Button>
      )}
    </div>
  );
}

// How was today?, from 18:00: what was on today, each Did it, Not this time or Another day (one tap each,
// with Undo; answering nothing changes nothing; Did it or Not this time tapped again, or Back in the plan, takes it
// back); a line to copy to my AI for a find of our own; and a photo of what we did.
function HowWasToday({ trip, asked, area }: { trip: string; asked: Item[]; area: string | null }) {
  const taps = useTapsHere();
  const id = useId();
  return (
    <section className="tm-today-evening" aria-labelledby={id}>
      <div className="tm-today-evening-head">
        <h2 id={id} className="display-m">
          How was today?
        </h2>
        <p className="callout tm-muted">Tap how each went. What you leave stays as it is.</p>
      </div>
      {asked.length > 0 && (
        <ul className="tm-today-asked">
          {asked.map((item) => (
            <li key={item.id} className="tm-today-ask">
              <span className="body-strong">{item.title}</span>
              <fieldset className="tm-today-answers" aria-label={`How ${item.title} went`}>
                <button
                  type="button"
                  className="tm-today-answer is-did"
                  aria-pressed={item.status === "done"}
                  onClick={(e) => (item.status === "done" ? taps.back(item) : taps.done(item, e))}
                >
                  Did{"\u00a0"}it
                </button>
                <button
                  type="button"
                  className="tm-today-answer"
                  aria-pressed={item.status === "missed"}
                  onClick={() => (item.status === "missed" ? taps.unmissed(item) : taps.missed(item))}
                >
                  Not this time
                </button>
                {item.type !== "move" && item.status !== "booked" && (
                  <button
                    type="button"
                    className="tm-today-answer"
                    aria-pressed={false}
                    onClick={() => taps.anotherDay(item)}
                  >
                    Another day
                  </button>
                )}
              </fieldset>
              {item.status === "done" && item.type !== "move" && <AddPhoto item={item} />}
              {item.status === "missed" && <BackInThePlan item={item} />}
            </li>
          ))}
        </ul>
      )}
      <div className="tm-today-find">
        <p className="callout">
          <strong>Found something of your own?</strong> Tell your AI:
        </p>
        <Prompt
          text={promptWith(
            promptLead(trip),
            `We found [a place] today${area ? ` near ${area}` : ""}: add it as done today, with where it is.`,
          )}
          label="What to tell your AI"
        />
      </div>
    </section>
  );
}

// Back in the plan, under a thing marked Not this time: the mark taken back (DESIGN.md, "What the web adds, it can
// take away").
function BackInThePlan({ item }: { item: Item }) {
  const taps = useTapsHere();
  return (
    <Button variant="quiet" size="sm" type="button" icon="replay" onClick={() => taps.unmissed(item)}>
      Back in the plan
    </Button>
  );
}

// Add a photo of something we did today: from the camera or the photo library on a phone.
function AddPhoto({ item }: { item: Item }) {
  const taps = useTapsHere();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*,.heic,.heif"
        hidden
        aria-label={`Choose a photo of ${item.title}`}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setBusy(true);
          await taps.photo(item, file);
          setBusy(false);
          e.target.value = "";
        }}
      />
      <Button
        variant="quiet"
        size="sm"
        type="button"
        icon="camera"
        disabled={busy}
        onClick={() => input.current?.click()}
      >
        {busy ? "Adding…" : "Add a photo"}
      </Button>
    </>
  );
}

// The day ("DAY 15 OF 36 · SAT 12 DEC"), where we are in display type and its weather: each city's on a
// moving day.
function TodayHead({
  picks,
  weather,
  mornings,
  headingId,
  now,
  children,
}: {
  picks: TodayPicks;
  weather: WeatherDay[] | null;
  mornings: WeatherDay[] | null;
  headingId: string;
  now: Date;
  children: ReactNode;
}) {
  const unit = useMemo<Unit>(
    () =>
      unitFor({
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        languages: navigator.languages?.length ? navigator.languages : [navigator.language],
      }),
    [],
  );
  const { morning, evening, moving } = picks;
  const title = moving && morning && evening ? `${morning.name} → ${evening.name}` : (evening ?? morning)?.label;
  const tonight = current(weather ?? [], now).get(picks.date) ?? null;
  const leaving = moving ? (current(mornings ?? [], now).get(picks.date) ?? null) : null;
  return (
    <header className="tm-today-head">
      <div className="tm-today-eyebrow">
        <p className="eyebrow tm-sun-text">{picks.dayLine}</p>
        {children}
      </div>
      {/* Focused as Today opens, so a screen reader starts here. */}
      <h1 id={headingId} className="display-l" tabIndex={-1} data-today-heading>
        {title ?? "Today"}
      </h1>
      {(tonight || leaving) && (
        <div className="tm-today-weather caption tm-muted">
          {leaving && (
            <WeatherLine
              day={leaving}
              unit={unit}
              name={moving ? (morning?.name ?? null) : null}
              slot={tonight?.kind === "forecast"}
            />
          )}
          {tonight && (
            <WeatherLine
              day={tonight}
              unit={unit}
              name={leaving ? (evening?.name ?? null) : null}
              slot={leaving?.kind === "forecast"}
            />
          )}
        </div>
      )}
    </header>
  );
}

// A day's weather in a line: the forecast with its icon, Usually without one. On a moving day with an icon on
// one line and not the other (`slot`), the line without keeps the icon's place, so the two start together.
function WeatherLine({ day, unit, name, slot }: { day: WeatherDay; unit: Unit; name: string | null; slot: boolean }) {
  const temps = `${degrees(day.high, unit)} / ${degrees(day.low, unit)}`;
  const words = day.kind === "forecast" ? `${SYMBOL_LABEL[day.symbol]} · ${temps}` : `Usually ${temps}`;
  return (
    <span className="tm-today-weather-line">
      {day.kind === "forecast" ? (
        <Icon name={SYMBOL_ICON[day.symbol]} size={16} />
      ) : (
        slot && <span className="tm-today-weather-slot" aria-hidden="true" />
      )}
      <span>{name ? `${name} · ${words}` : words}</span>
    </span>
  );
}

// Next up: the one timed thing still to come today (or the day booked whole), with its tickets or its booking
// one tap away.
function NextUpCard({
  next,
  now,
  files,
  onOpen,
}: {
  next: NextUp;
  now: Date;
  files: TripFile[];
  onOpen: (id: string) => void;
}) {
  const { item } = next;
  const when =
    next.kind === "allday"
      ? "all day"
      : next.kind === "checkout"
        ? `by ${next.time} · ${untilWords(next.at - now.getTime())}`
        : next.kind === "checkin"
          ? `from ${next.time} · ${untilWords(next.at - now.getTime())}`
          : `${next.time} · ${untilWords(next.at - now.getTime())}`;
  const title =
    next.kind === "checkout"
      ? `Check out of ${item.title}`
      : next.kind === "checkin"
        ? `Check in at ${item.title}`
        : item.title;
  const facts = [
    item.booking?.ref && bookedAlready(item) ? `ref ${item.booking.ref}` : null,
    item.booking?.provider ?? null,
    next.kind === "timed" || next.kind === "allday" ? itemKind(item) : null,
  ];
  return (
    <section className="tm-today-next" aria-label="Next up">
      <p className="caption tm-today-next-when">Next up · {when}</p>
      <h2 className="display-m">{title}</h2>
      {facts.some(Boolean) && <p className="callout">{factLine(facts)}</p>}
      <div className="tm-today-buttons">
        <Tickets item={item} files={files} onOpen={onOpen} primary />
      </div>
    </section>
  );
}

// Its tickets (the files on it, kept on this device), or its booking: one tap. One picture opens full screen,
// one PDF in a new tab; several, or the booking alone, open its card.
function Tickets({
  item,
  files,
  onOpen,
  primary = false,
}: {
  item: Item;
  files: TripFile[];
  onOpen: (id: string) => void;
  primary?: boolean;
}) {
  const drawsHeic = useDrawsHeic();
  const [viewing, setViewing] = useState<TripFile | null>(null);
  const variant = primary ? "primary" : "secondary";
  const [only] = files;
  if (files.length === 1 && only && drawable(only, drawsHeic))
    return (
      <>
        <Button variant={variant} size="sm" type="button" icon="book" onClick={() => setViewing(only)}>
          Tickets
        </Button>
        {viewing && <Viewer file={viewing} onClose={() => setViewing(null)} />}
      </>
    );
  if (files.length === 1 && only)
    return (
      <a
        className={`sv-btn sv-btn--${variant} sv-btn--sm tm-button-link`}
        href={fileHref(only.id)}
        target="_blank"
        rel="noreferrer"
      >
        <span>Tickets</span>
      </a>
    );
  // Booking: its card, where its booking is; not for one kept from a booking that wasn't (Not booked after all).
  if (files.length === 0 && !(item.booking && bookedAlready(item))) return null;
  return (
    <Button variant={variant} size="sm" type="button" onClick={() => onOpen(item.id)}>
      {files.length > 0 ? "Tickets" : "Booking"}
    </Button>
  );
}

const PART: Record<string, string> = { morning: "Morning", afternoon: "Afternoon", evening: "Evening", night: "Night" };

// A line of what's on today: what it is and when; a check-out or check-in; a pass's day.
function TodayLine({ row, onOpen }: { row: Exclude<TodayRow, { kind: "todo" }>; onOpen: (id: string) => void }) {
  const { item } = row;
  const title =
    row.kind === "checkout"
      ? `Check out · ${item.title}`
      : row.kind === "checkin"
        ? `Check in · ${item.title}`
        : row.kind === "pass" && row.of > 1
          ? `${item.title} · day ${row.day} of ${row.of}`
          : item.title;
  const when =
    row.kind === "checkout"
      ? `by ${row.time}`
      : row.kind === "checkin"
        ? `from ${row.time}`
        : row.kind === "item"
          ? (row.time ?? (row.part ? PART[row.part] : "Any time"))
          : null;
  const facts =
    row.kind === "item"
      ? [
          when,
          itemKind(item),
          item.status === "booked"
            ? "booked"
            : item.status === "done"
              ? "done"
              : item.status === "missed"
                ? "not this time"
                : null,
          item.hours ?? null,
        ]
      : [when];
  if (row.kind === "item") return <TickLine item={item} title={title} facts={facts} onOpen={onOpen} />;
  return (
    <li>
      <button
        type="button"
        className={`tm-today-row${row.kind === "pass" ? " is-pass" : ""}`}
        onClick={() => onOpen(item.id)}
      >
        <KindIcon item={item} />
        <span className="tm-today-text">
          <span className="body-strong">{title}</span>
          {facts.some(Boolean) && <span className="caption tm-muted">{factLine(facts)}</span>}
        </span>
      </button>
    </li>
  );
}

// Something on today, with its tick (done, and back); for a place or goal not booked, Another day; and marked Not this
// time, Back in the plan.
function TickLine({
  item,
  title,
  facts,
  onOpen,
}: {
  item: Item;
  title: string;
  facts: (string | null | undefined)[];
  onOpen: (id: string) => void;
}) {
  const taps = useTapsHere();
  const readOnly = useReadOnly();
  const done = item.status === "done";
  const later = !done && !readOnly && item.type !== "move" && item.status !== "booked";
  const back = !readOnly && item.status === "missed";
  const icon = (
    <span className={`tm-row-icon tm-task-icon${done ? " is-done" : ""}`} aria-hidden="true">
      {done && <Icon name="check" size={18} />}
    </span>
  );
  return (
    <li>
      <div className={`tm-today-row has-tick${done ? " is-done" : ""}`}>
        {readOnly ? (
          <span className="tm-today-tick is-still">{icon}</span>
        ) : (
          <button
            type="button"
            className="tm-today-tick"
            aria-pressed={done}
            aria-label={`Done: ${title}`}
            onClick={(e) => (done ? taps.back(item) : taps.done(item, e))}
          >
            {icon}
          </button>
        )}
        <div className="tm-today-main">
          <button type="button" className="tm-today-open" onClick={() => onOpen(item.id)}>
            <span className="tm-today-text">
              <span className="body-strong">{title}</span>
              {facts.some(Boolean) && <span className="caption tm-muted">{factLine(facts)}</span>}
            </span>
          </button>
          {(later || back) && (
            <div className="tm-today-laters">
              {back && (
                <button
                  type="button"
                  className="tm-today-later caption"
                  aria-label={`Back in the plan: ${title}`}
                  onClick={() => taps.unmissed(item)}
                >
                  Back in the plan
                </button>
              )}
              {later && (
                <button
                  type="button"
                  className="tm-today-later caption"
                  aria-label={`Another day: ${title}`}
                  onClick={() => taps.anotherDay(item)}
                >
                  Another day
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

// Around you, or around a stay's area: what someone said Yes or Maybe to, yeses first, four at a time; on a
// moving day the morning's area first and the evening's folded; folded on a day something booked fills.
function Around({
  picks,
  trip,
  me,
  onOpen,
  onAllOnMap,
}: {
  picks: TodayPicks;
  trip: TripView;
  me: boolean;
  onOpen: (id: string) => void;
  onAllOnMap: (ids: string[]) => void;
}) {
  const around = picks.around;
  const [open, setOpen] = useState(false);
  const id = useId();
  if (!around) return null;
  const [first, second] = around.lists;
  if (!first) return null;
  const heading =
    around.mode === "you"
      ? "Around you"
      : around.mode === "moving"
        ? `Before you leave ${first.area?.name ?? ""}`
        : `Around ${first.area?.name ?? ""}`;
  const wanted = (l: AroundList) => l.rows.length;
  const body = (list: AroundList) => <AroundRows list={list} trip={trip} onOpen={onOpen} onAllOnMap={onAllOnMap} />;
  if (around.full) {
    const count = around.lists.reduce((n, l) => n + wanted(l), 0);
    return (
      <section className="tm-today-section" aria-label={heading}>
        <FoldButton
          open={open}
          controls={`${id}-full`}
          onToggle={() => setOpen(!open)}
          title="Today is full, so these wait here"
          caption={`${count} ${count === 1 ? "thing" : "things"} wanted ${me ? "around you" : `around ${first.area?.name ?? "here"}`}`}
        />
        {open && <div id={`${id}-full`}>{body(first)}</div>}
      </section>
    );
  }
  return (
    <>
      <section className="tm-today-section" aria-labelledby={`${id}-head`}>
        <h2 id={`${id}-head`} className="title">
          {heading}
        </h2>
        {wanted(first) === 0 ? <p className="callout tm-muted">Nothing anyone wants here yet.</p> : body(first)}
      </section>
      {around.mode === "moving" && second && (
        <section className="tm-today-section" aria-label={`This evening, around ${second.area?.name ?? ""}`}>
          <FoldButton
            open={open}
            controls={`${id}-evening`}
            onToggle={() => setOpen(!open)}
            title={`This evening, around ${second.area?.name ?? ""}`}
            caption={
              wanted(second) === 0
                ? "Nothing anyone wants there yet"
                : `${second.rows[0]?.item.title}${wanted(second) > 1 ? ` and ${wanted(second) - 1} more` : ""}`
            }
          />
          {open && <div id={`${id}-evening`}>{body(second)}</div>}
        </section>
      )}
    </>
  );
}

function FoldButton({
  open,
  controls,
  onToggle,
  title,
  caption,
}: {
  open: boolean;
  controls: string;
  onToggle: () => void;
  title: string;
  caption: string;
}) {
  return (
    <button
      type="button"
      className={`tm-today-fold${open ? " is-open" : ""}`}
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
    >
      <span className="tm-today-text">
        <span className="callout">
          <strong>{title}</strong>
        </span>
        <span className="caption tm-muted">{caption}</span>
      </span>
      <Icon name="chevron" size={20} className="tm-today-caret" />
    </button>
  );
}

function AroundRows({
  list,
  trip,
  onOpen,
  onAllOnMap,
}: {
  list: AroundList;
  trip: TripView;
  onOpen: (id: string) => void;
  onAllOnMap: (ids: string[]) => void;
}) {
  const [shown, setShown] = useState(AT_A_TIME);
  const rest = list.rows.length - shown;
  const all = list.rows.map((r) => r.via?.id ?? r.item.id);
  return (
    <>
      {list.rows.length > 0 && (
        <ul className="tm-today-rows">
          {list.rows.slice(0, shown).map((row) => (
            <AroundLine key={row.item.id} row={row} trip={trip} onOpen={onOpen} />
          ))}
        </ul>
      )}
      <div className="tm-today-more">
        {rest > 0 ? (
          <button type="button" className="tm-link-button callout" onClick={() => setShown(shown + AT_A_TIME)}>
            {rest} more
          </button>
        ) : (
          <span />
        )}
        {all.length > 0 && (
          <button type="button" className="tm-link-button callout" onClick={() => onAllOnMap(all)}>
            All on the map
          </button>
        )}
      </div>
    </>
  );
}

// A place or goal we want: whose wanting it is, how far, and its hours and best time as written (Tripmo never
// reads them); a goal by its nearest candidate.
function AroundLine({ row, trip, onOpen }: { row: AroundRow; trip: TripView; onOpen: (id: string) => void }) {
  const { item } = row;
  const facts: ReactNode[] = [
    <strong key="who" className={`tm-today-who is-${row.tier}`}>
      {whoseWords(row, trip.you)}
    </strong>,
    row.m !== null ? km(row.m) : null,
    row.via ? `at ${row.via.title}` : null,
    item.hours ?? null,
    item.best_time ? `best: ${item.best_time}` : null,
  ];
  const taps = useTapsHere();
  const readOnly = useReadOnly();
  return (
    <li>
      <div className={`tm-today-row${readOnly ? "" : " has-action"}`}>
        <button type="button" className="tm-today-open" onClick={() => onOpen(item.id)}>
          <KindIcon item={row.via ?? item} />
          <span className="tm-today-text">
            <span className="body-strong">{item.title}</span>
            <span className="caption tm-muted">{factLine(facts)}</span>
          </span>
        </button>
        {!readOnly && (
          <button
            type="button"
            className="tm-today-add"
            aria-label={`Put ${item.title} on today`}
            onClick={() => taps.onToday(item)}
          >
            + Today
          </button>
        )}
      </div>
    </li>
  );
}

// Where an area is, in a sentence: "in Osaka", or "near Stanford Hotel" for a stay with no city.
const inArea = (label: string) => (label.startsWith("Near ") ? `near ${label.slice(5)}` : `in ${label}`);

// "Osaka Castle park, Bathhouse world and Kiji Umeda and 2 more."
function rateNames(ids: string[], items: Item[]): string {
  const names = ids.slice(0, 3).flatMap((id) => items.find((i) => i.id === id)?.title ?? []);
  const more = ids.length - names.length;
  const listed = names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
  return more > 0 ? `${names.join(", ")} and ${more} more.` : `${listed}.`;
}

// Tonight's stay: which night of how many, its address, check-in when I arrive today (from its times),
// Directions (the phone's maps at its place), Show the address (large, for a taxi) and its booking.
function Tonight({ tonight, onOpen }: { tonight: NonNullable<TodayPicks["tonight"]>; onOpen: (id: string) => void }) {
  const { stay } = tonight;
  const [showing, setShowing] = useState(false);
  const at = point(stay);
  const address = stay.where?.address ?? null;
  const facts = [
    `Night ${tonight.night} of ${tonight.nights}`,
    tonight.checkIn ? `check in from ${tonight.checkIn}` : null,
    address,
  ];
  return (
    <section className="tm-today-section" aria-label="Tonight">
      <h2 className="title">Tonight</h2>
      <div className="tm-today-tonight">
        <button type="button" className="tm-today-row is-flat" onClick={() => onOpen(stay.id)}>
          <KindIcon item={stay} />
          <span className="tm-today-text">
            <span className="body-strong">{stay.title}</span>
            <span className="caption tm-muted">{factLine(facts)}</span>
          </span>
        </button>
        <div className="tm-today-actions">
          {at && (
            <a
              className="sv-btn sv-btn--secondary sv-btn--sm tm-button-link"
              href={directions(at)}
              target="_blank"
              rel="noreferrer"
            >
              <span>Directions</span>
            </a>
          )}
          {(address || stay.where?.name) && (
            <Button variant="secondary" size="sm" type="button" onClick={() => setShowing(true)}>
              Show the address
            </Button>
          )}
          {stay.booking && (
            <Button variant="secondary" size="sm" type="button" onClick={() => onOpen(stay.id)}>
              Booking
            </Button>
          )}
        </div>
      </div>
      {showing && <ShowAddress stay={stay} onClose={() => setShowing(false)} />}
    </section>
  );
}

// The address, large, to show a taxi driver.
function ShowAddress({ stay, onClose }: { stay: Item; onClose: () => void }) {
  return (
    <Modal title={stay.title} busy={false} focus=".tm-address-close" onCancel={onClose} className="tm-address">
      {({ describedBy }) => (
        <>
          <p id={describedBy} className="tm-address-text">
            {stay.where?.address ?? stay.where?.name}
          </p>
          {stay.where?.address && stay.where.name && stay.where.name !== stay.title && (
            <p className="title tm-muted">{stay.where.name}</p>
          )}
          <div className="tm-dialog-actions">
            <Button variant="primary" size="md" type="button" className="tm-address-close" onClick={onClose}>
              Done
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

// The phone's own maps, with directions to a place: Apple's on an iPhone, iPad or Mac, else Google's (which
// opens its app on Android).
function directions([lng, lat]: LngLat): string {
  const apple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
  return apple
    ? `https://maps.apple.com/?daddr=${lat},${lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}

// Tomorrow in a few words: its first fixed thing, else moving on to another stay, else nothing fixed.
function tomorrowWords(tomorrow: NonNullable<TodayPicks["tomorrow"]>): string {
  const day = weekday(tomorrow.date);
  const { first, onTo } = tomorrow;
  if (first) {
    const what =
      first.kind === "checkout"
        ? `check out of ${first.item.title} by ${first.time}`
        : first.kind === "checkin"
          ? `check in at ${first.item.title} from ${first.time}`
          : first.kind === "allday"
            ? `${first.item.title}, all day`
            : `${first.time} ${first.item.title}`;
    return `${day} · ${what}`;
  }
  if (onTo) return `${day} · on to ${onTo.name}`;
  return `${day} · nothing fixed`;
}
